import { useRef } from 'react'
import { useWebMfa } from '../hooks/use-web-mfa'
import { LockIcon } from './lock-icon'
import { BRAND_LOGO_URL } from '../../../utils/brand-logo'
import { APP_LOCK_Z_CLASS } from '../../../utils/call-overlay-layers'
import { useFocusTrap } from '../../../hooks/use-focus-trap'

/**
 * Web weekly MFA gate ("Verify it's you"): password + emailed OTP.
 *
 * Mounted once near the app root (`NativeBootstrap`). Same z-index / soft-lock
 * UX as the native biometric sheet — call overlays stay above. Native builds
 * never show this (12h biometric soft-lock is separate).
 */
export function WebMfaChallengeScreen() {
  const {
    showSheet,
    step,
    password,
    setPassword,
    code,
    setCode,
    destinationHint,
    busy,
    errorMessage,
    resendIn,
    verifyCopy,
    submitPassword,
    submitCode,
    resend,
    backToPassword,
    signOut,
  } = useWebMfa()
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, showSheet)

  if (!showSheet) return null

  return (
    <div
      className={`pointer-events-auto fixed inset-0 ${APP_LOCK_Z_CLASS} flex items-end justify-center bg-slate-900/25 p-4 pb-[max(1rem,var(--safe-bottom))] backdrop-blur-[2px] sm:items-center touch-none`}
      role="dialog"
      aria-modal="true"
      aria-label={verifyCopy}
      ref={dialogRef}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="pointer-events-auto w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <img
          src={BRAND_LOGO_URL}
          alt=""
          className="mx-auto h-12 w-12 rounded-2xl bg-brand-50 p-1.5"
          aria-hidden
        />
        <div
          className="mx-auto mt-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-700"
          aria-hidden
        >
          {busy ? (
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
          ) : (
            <LockIcon className="h-7 w-7" />
          )}
        </div>
        <h1 className="mt-4 text-lg font-bold text-slate-900">{verifyCopy}</h1>
        <p className="mt-1 text-sm text-slate-600" role={errorMessage ? 'alert' : 'status'} aria-live="polite">
          {errorMessage
            ? errorMessage
            : step === 'password'
              ? 'Confirm your password, then we\u2019ll email a one-time code. Required every 7 days on the web.'
              : destinationHint
                ? `Enter the 6-digit code we sent to ${destinationHint}.`
                : 'Enter the 6-digit code from your email.'}
        </p>

        {step === 'password' ? (
          <form
            className="mt-5 flex w-full flex-col gap-2 text-left"
            onSubmit={(e) => {
              e.preventDefault()
              void submitPassword()
            }}
          >
            <label className="block text-xs font-semibold text-slate-700" htmlFor="web-mfa-password">
              Password
            </label>
            <input
              id="web-mfa-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 disabled:opacity-60"
              aria-required
            />
            <button
              type="submit"
              disabled={busy || !password}
              className="mt-2 w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              {busy ? 'Sending code\u2026' : 'Continue'}
            </button>
          </form>
        ) : (
          <form
            className="mt-5 flex w-full flex-col gap-2 text-left"
            onSubmit={(e) => {
              e.preventDefault()
              void submitCode()
            }}
          >
            <label className="block text-xs font-semibold text-slate-700" htmlFor="web-mfa-code">
              Email code
            </label>
            <input
              id="web-mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={busy}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-center text-lg font-semibold tracking-[0.2em] text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 disabled:opacity-60"
              aria-required
            />
            <button
              type="submit"
              disabled={busy || code.replace(/\s+/g, '').length < 6}
              className="mt-2 w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              {busy ? 'Verifying\u2026' : 'Verify'}
            </button>
            <div className="mt-1 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={backToPassword}
                disabled={busy}
                className="text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => void resend()}
                disabled={busy || resendIn > 0}
                className="text-xs font-semibold text-brand-700 hover:text-brand-800 disabled:opacity-60"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        <button
          type="button"
          onClick={() => void signOut()}
          disabled={busy}
          className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
